import { Link } from "react-router-dom";
import { SHOP } from "@/constants/testIds";
import { resolveImage } from "@/lib/siteDefaults";
import WishlistHeart from "@/components/WishlistHeart";
import { EarlyAccessBadge } from "@/components/community/EarlyAccess";

export default function ProductCard({ product, index = 0 }) {
  const img = (product.images || [])[0];
  // Determine sold-out / low-stock across per-size or single stock
  const sbs = product.stock_by_size;
  const usesPerSize = sbs && Object.keys(sbs).length > 0;
  let soldOut = false;
  let lowStock = false;
  let lowStockCount = null;
  if (usesPerSize) {
    const sizes = product.sizes || [];
    const trackedValues = sizes
      .filter((s) => s in sbs)
      .map((s) => Number(sbs[s]));
    const untrackedSizes = sizes.filter((s) => !(s in sbs));
    // Sold out only when every listed size is tracked AND all are 0
    if (untrackedSizes.length === 0 && trackedValues.length > 0 && trackedValues.every((v) => v === 0)) {
      soldOut = true;
    } else if (untrackedSizes.length === 0) {
      const remainingTotal = trackedValues.reduce((a, b) => a + b, 0);
      if (remainingTotal > 0 && remainingTotal <= 5) {
        lowStock = true;
        lowStockCount = remainingTotal;
      }
    }
  } else {
    soldOut = product.stock != null && product.stock <= 0;
    lowStock = product.stock != null && product.stock > 0 && product.stock <= 5;
    lowStockCount = lowStock ? product.stock : null;
  }
  return (
    <Link
      to={`/product/${product.id}`}
      data-testid={SHOP.productCard}
      className="group relative flex flex-col fade-up"
      style={{ animationDelay: `${index * 60}ms` }}
    >
      <div className="aspect-[4/5] overflow-hidden rounded-2xl bg-[#F4F1EA] mb-4 relative">
        {img ? (
          <img
            src={resolveImage(img)}
            alt={product.name}
            loading="lazy"
            data-testid={SHOP.productImage}
            className={`w-full h-full object-cover group-hover:scale-105 transition-transform duration-[900ms] ease-out ${soldOut ? "opacity-60 grayscale" : ""}`}
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-stone-400 font-serif italic">
            {product.name}
          </div>
        )}
        <span className="absolute top-4 left-4 text-[10px] uppercase tracking-[0.2em] px-2.5 py-1 bg-white/70 backdrop-blur text-stone-700 rounded-full">
          {product.category === "gym" ? "Performance" : "Beauty"}
        </span>
        <EarlyAccessBadge productId={product.id} />
        {soldOut && (
          <span className="absolute bottom-4 left-4 text-[10px] uppercase tracking-[0.2em] px-3 py-1.5 bg-[#1A1C1A] text-[#F9F9F6] rounded-full font-semibold">
            Sold out
          </span>
        )}
        {!soldOut && lowStock && (
          <span className="absolute bottom-4 left-4 text-[10px] uppercase tracking-[0.2em] px-3 py-1.5 bg-white/90 backdrop-blur text-[#8B6A1A] rounded-full font-semibold">
            Only {lowStockCount} left
          </span>
        )}
        <div className="absolute top-3 right-3">
          <WishlistHeart productId={product.id} size="sm" />
        </div>
      </div>
      <div className="flex items-start justify-between gap-3 px-1">
        <div>
          <h3
            data-testid={SHOP.productTitle}
            className="font-serif text-xl md:text-2xl font-light text-[#1A1C1A] leading-tight tracking-tight"
          >
            {product.name}
          </h3>
          {product.colors?.length > 0 && (
            <p className="mt-1 text-xs text-stone-500 font-sans">{product.colors.length} color{product.colors.length > 1 ? "s" : ""}</p>
          )}
        </div>
        <p data-testid={SHOP.productPrice} className="font-sans text-sm text-[#1A1C1A] whitespace-nowrap pt-1">
          ${product.price.toFixed(2)}
        </p>
      </div>
    </Link>
  );
}
